/**
 * Claim-a-source dialog (Phase 2) — an end-user pastes a claim code from
 * their platform administrator to adopt a (simulated) device source into
 * the active org. Calls POST /v1/things/claim, then invalidates the things
 * query so the new gateway + children appear immediately.
 */

import { useState } from "react";
import { Loader2, PackagePlus } from "lucide-react";

import { things } from "@/lib/api";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { useToast } from "@/lib/ui/toast";

function claimErrorMessage(code: string | undefined): string {
  switch (code) {
    case "unknown_claim_code":
      return "That claim code wasn't found.";
    case "claim_already_consumed":
      return "That claim code was already used.";
    case "claim_expired":
      return "That claim code has expired.";
    case "not_a_factory_source":
      return "That code isn't for a claimable device source.";
    case "missing_claim_code":
      return "Enter a claim code.";
    default:
      return "Couldn't claim the source. Check the code and try again.";
  }
}

export function ClaimSourceDialog({
  open,
  onClose,
  onClaimed,
}: {
  open: boolean;
  onClose: () => void;
  onClaimed: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = () => {
    const c = code.trim();
    if (!c) {
      toast.push("error", "Enter a claim code.");
      return;
    }
    setBusy(true);
    things
      .adopt(c)
      .then((res) => {
        toast.push(
          "ok",
          `Source claimed — ${res.device_count} device${res.device_count === 1 ? "" : "s"} added to this org`,
        );
        setCode("");
        onClaimed();
        onClose();
      })
      .catch((err: unknown) => {
        const errCode =
          typeof err === "object" && err && "body" in err
            ? (err as { body?: { error?: string } }).body?.error
            : undefined;
        toast.push("error", claimErrorMessage(errCode));
      })
      .finally(() => setBusy(false));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Claim a device source"
      >
        <div className="mb-3 flex items-center gap-2">
          <PackagePlus className="size-5 text-brand" />
          <h2 className="text-lg font-semibold text-on-surface">
            Claim a device source
          </h2>
        </div>
        <p className="mb-4 text-sm text-on-surface-muted">
          Paste the claim code from your platform administrator to adopt a
          device source into this organisation. Its devices, tags and live data
          will appear here.
        </p>
        <Label htmlFor="claim-code">Claim code</Label>
        <Input
          id="claim-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="SIM-xxxxxxxxxxxxxxxxxx"
          className="mt-1 font-mono"
          autoFocus
        />
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !code.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            Claim source
          </Button>
        </div>
      </div>
    </div>
  );
}
